import { logMessage } from 'helpers/loggers';

const STORAGE_KEY = 'chunkLoadRecoveryAt';

// a second failure this soon means the reload did not help, so the error page stays
const RECOVERY_WINDOW_MS = 30_000;

// the error page is already showing, so a hung refresh must not hold the reload up for long
const REFRESH_TIMEOUT_MS = 5_000;

const URL_IN_MESSAGE = /https?:\/\/[^\s"')]+/;

/** Chrome names the failing file in the message; Safari and Firefox do not. */
export function getFailedChunkUrl(error: unknown) {
  const message = error instanceof Error ? error.message : '';

  return message.match(URL_IN_MESSAGE)?.[0];
}

function isSameOrigin(url: string) {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

// 'recent' means a reload just happened; without storage there is no loop guard, so 'unavailable'
function claimRecovery(): 'claimed' | 'recent' | 'unavailable' {
  try {
    const lastAttemptAt = Number(window.sessionStorage.getItem(STORAGE_KEY));

    if (Date.now() - lastAttemptAt < RECOVERY_WINDOW_MS) return 'recent';

    window.sessionStorage.setItem(STORAGE_KEY, String(Date.now()));

    return 'claimed';
  } catch {
    return 'unavailable';
  }
}

// a plain AbortController, since AbortSignal.timeout is missing from older Safari
async function refreshCachedFile(url: string) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

  await fetch(url, { cache: 'reload', signal: controller.signal }).catch(() => {});

  // a no-op when the abort timer already fired
  clearTimeout(timer);
}

/** Resolves false when it left the page alone, because it already reloaded a moment ago. */
export async function recoverFromChunkLoadError(
  error: unknown,
  reload: () => void = () => window.location.reload(),
) {
  // offline, a reload would only swap the error page for the browser's own offline page
  if (!window.navigator.onLine) return false;

  const claim = claimRecovery();
  const url = getFailedChunkUrl(error);

  // a failure this soon after a reload is the retry failing, so it gets its own tag
  if (claim === 'recent') {
    logMessage('Chunk load failed again after a reload', { url }, 'warning', { attempt: 'retry' });

    return false;
  }

  if (claim !== 'claimed') return false;

  logMessage('Reloading after a chunk load failure', { url }, 'warning', { attempt: 'first' });

  // a bad response may be cached for a day, and a plain reload would reuse it
  if (url && isSameOrigin(url)) await refreshCachedFile(url);

  reload();

  return true;
}

type Reload = (path?: string) => void;

// the router has not moved yet, so the reload has to go to the route the user clicked
const reloadTo: Reload = path => {
  if (path) window.location.assign(path);
  else window.location.reload();
};

/** Reloads once when a lazy chunk fails to load, for example after a deploy removed it. */
export function registerChunkLoadRecovery(
  getPendingPath: () => string | undefined = () => undefined,
  reload: Reload = reloadTo,
) {
  // not prevented: the import still rejects, so the error page shows if the reload does not help
  const onPreloadError = (event: Event) => {
    // read now, since the navigation can settle while the file is refreshed
    const target = getPendingPath();

    void recoverFromChunkLoadError((event as Event & { payload: unknown }).payload, () =>
      reload(target),
    );
  };

  window.addEventListener('vite:preloadError', onPreloadError);

  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
